// Utility functions for Pakistan Standard Time (PST) handling

/**
 * Get current time in Pakistan Standard Time (PST) with 12-hour format
 * @returns {string} Time in format "HH:MM:SS AM/PM"
 */
export const getPakistanTime = () => {
  const now = new Date();
  return now.toLocaleTimeString('en-US', {
    timeZone: 'Asia/Karachi',
    hour12: true,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

/**
 * Get current date in Pakistan Standard Time (PST)
 * @returns {string} Date in format "YYYY-MM-DD"
 */
export const getPakistanDate = () => {
  const now = new Date();
  return now.toLocaleDateString('en-CA', {
    timeZone: 'Asia/Karachi'
  });
};

/**
 * Get current date and time in Pakistan Standard Time (PST)
 * @returns {object} Object with date and time properties
 */
export const getPakistanDateTime = () => {
  const now = new Date();
  return {
    date: now.toLocaleDateString('en-CA', {
      timeZone: 'Asia/Karachi'
    }),
    time: now.toLocaleTimeString('en-US', {
      timeZone: 'Asia/Karachi',
      hour12: true,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  };
};

/**
 * Format a time string to ensure it's in 12-hour format with AM/PM
 * @param {string} timeStr - Time string to format
 * @returns {string} Formatted time string
 */
export const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  
  // If already in 12-hour format with AM/PM, return as is
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    return timeStr;
  }
  
  // Try to parse and format the time
  try {
    // Handle 24-hour format
    const timeMatch = timeStr.match(/(\d{1,2}):(\d{2}):(\d{2})/);
    if (timeMatch) {
      const [_, hours, minutes, seconds] = timeMatch;
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${hour12.toString().padStart(2, '0')}:${minutes}:${seconds} ${ampm}`;
    }
  } catch (error) {
    console.error('Error formatting time:', error);
  }
  
  return timeStr;
};

/**
 * Calculate duration between two time strings (12-hour format)
 * @param {string} checkInTime - Check-in time
 * @param {string} checkOutTime - Check-out time
 * @returns {string} Duration in format "Xh Ym"
 */
export const calculateDuration = (checkInTime, checkOutTime) => {
  try {
    // Parse 12-hour time format (e.g., "2:30:45 PM")
    const parseTime = (timeStr) => {
      if (!timeStr) return null;
      
      const match = timeStr.match(/(\d{1,2}):(\d{2}):(\d{2}) ?([APap][Mm])?/);
      if (!match) return null;
      
      let [_, hours, minutes, seconds, ampm] = match;
      hours = parseInt(hours, 10);
      minutes = parseInt(minutes, 10);
      seconds = parseInt(seconds, 10);
      
      // Convert to 24-hour format
      if (ampm) {
        if (ampm.toLowerCase() === 'pm' && hours !== 12) {
          hours += 12;
        }
        if (ampm.toLowerCase() === 'am' && hours === 12) {
          hours = 0;
        }
      }
      
      return { hours, minutes, seconds };
    };
    
    const inTime = parseTime(checkInTime);
    const outTime = parseTime(checkOutTime);
    
    if (!inTime || !outTime) return '';
    
    // Create Date objects for calculation
    const start = new Date();
    start.setHours(inTime.hours, inTime.minutes, inTime.seconds, 0);
    
    const end = new Date();
    end.setHours(outTime.hours, outTime.minutes, outTime.seconds, 0);
    
    // Handle overnight (if checkout is before checkin, assume next day)
    if (end < start) {
      end.setDate(end.getDate() + 1);
    }
    
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  } catch (error) {
    console.error('Error calculating duration:', error);
    return '';
  }
};

/**
 * Get Pakistan timezone offset for display purposes
 * @returns {string} Timezone string
 */
export const getPakistanTimezone = () => {
  return 'Asia/Karachi (PST)';
};
